class MimeTypesFactory<T extends string[]> extends Array<T[number]> {
  constructor(...init: T) {
    super(...init);
  }

  private append<N extends string[]>(...mimeTypes: N) {
    return new MimeTypesFactory<[...T, ...N][number][]>(
      ...this.values(),
      ...mimeTypes,
    );
  }

  // Image types
  image() {
    return this.append("image/*" as const);
  }

  png() {
    return this.append("image/png" as const, "image/x-png" as const);
  }

  jpg() {
    return this.append("image/jpeg" as const, "image/jpg" as const);
  }

  gif() {
    return this.append("image/gif" as const);
  }

  svg() {
    return this.append("image/svg+xml" as const);
  }

  webp() {
    return this.append("image/webp" as const);
  }

  ico() {
    return this.append(
      "image/x-icon" as const,
      "image/vnd.microsoft.icon" as const,
    );
  }

  // Video types
  video() {
    return this.append("video/*" as const);
  }

  mp4() {
    return this.append("video/mp4" as const);
  }

  webm() {
    return this.append("video/webm" as const);
  }

  mov() {
    return this.append("video/quicktime" as const);
  }

  avi() {
    return this.append("video/x-msvideo" as const);
  }

  // Audio types
  audio() {
    return this.append("audio/*" as const);
  }

  mp3() {
    return this.append("audio/mpeg" as const, "audio/mp3" as const);
  }

  wav() {
    return this.append("audio/wav" as const, "audio/wave" as const);
  }

  ogg() {
    return this.append("audio/ogg" as const);
  }

  aac() {
    return this.append("audio/aac" as const);
  }

  // Document types
  pdf() {
    return this.append("application/pdf" as const);
  }

  doc() {
    return this.append(
      "application/msword" as const,
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" as const,
    );
  }

  xls() {
    return this.append(
      "application/vnd.ms-excel" as const,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" as const,
    );
  }

  ppt() {
    return this.append(
      "application/vnd.ms-powerpoint" as const,
      "application/vnd.openxmlformats-officedocument.presentationml.presentation" as const,
    );
  }

  // Archive types
  zip() {
    return this.append(
      "application/zip" as const,
      "application/x-zip-compressed" as const,
      "application/x-compressed" as const,
      "application/x-compressed-zip" as const,
    );
  }

  rar() {
    return this.append(
      "application/x-rar-compressed" as const,
      "application/rar" as const,
    );
  }

  // Text types
  text() {
    return this.append("text/*" as const);
  }

  plain() {
    return this.append("text/plain" as const);
  }

  txt() {
    return this.append("text/plain" as const);
  }

  csv() {
    return this.append("text/csv" as const);
  }

  xlsx() {
    return this.append(
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" as const,
      "application/vnd.ms-excel" as const,
    );
  }

  pptx() {
    return this.append(
      "application/vnd.openxmlformats-officedocument.presentationml.presentation" as const,
      "application/vnd.ms-powerpoint" as const,
    );
  }

  html() {
    return this.append("text/html" as const);
  }

  css() {
    return this.append("text/css" as const);
  }

  // Code types
  javascript() {
    return this.append(
      "application/javascript" as const,
      "text/javascript" as const,
    );
  }

  typescript() {
    return this.append(
      "application/typescript" as const,
      "text/typescript" as const,
    );
  }

  json() {
    return this.append("application/json" as const);
  }

  xml() {
    return this.append("application/xml" as const, "text/xml" as const);
  }

  // Font types
  font() {
    return this.append("font/*" as const);
  }

  ttf() {
    return this.append("font/ttf" as const, "application/x-font-ttf" as const);
  }

  woff() {
    return this.append("font/woff" as const, "application/font-woff" as const);
  }

  woff2() {
    return this.append(
      "font/woff2" as const,
      "application/font-woff2" as const,
    );
  }

  // 3D types
  gltf() {
    return this.append(
      "model/gltf+json" as const,
      "model/gltf-binary" as const,
    );
  }

  obj() {
    return this.append("model/obj" as const);
  }

  // CAD types
  dwg() {
    return this.append("application/acad" as const, "application/dwg" as const);
  }

  dxf() {
    return this.append("application/dxf" as const);
  }

  custom(...mimeTypes: string[]) {
    return this.append(...mimeTypes);
  }

  includes(type: string) {
    return this.some((mime) => mime === type);
  }
}

export const mimeTypes = new MimeTypesFactory<[]>();
